import { useEffect, useRef, type HTMLAttributes } from "react";
import { useLocation } from "wouter";

import type { AnalyticsElementId } from "@/lib/analytics/analyticsElementId";
import { deriveAnalyticsElementId } from "@/lib/analytics/deriveAnalyticsElementId";
import {
  homeNavigationLink,
  primaryNavigationLinks,
  publicNavigationDesktopQuery,
  signInNavigationLink,
} from "@/lib/navigationConfig";
import { cn } from "@/lib/utils";
import { Logo } from "@/shared/design-system/Logo";
import { MobileNavigation } from "./MobileNavigation";
import { PublicNavigationLink } from "./PublicNavigationLink";
import { focusPublicMain } from "./focusPublicMain";

import "./navigation.css";

type NavigationProps = HTMLAttributes<HTMLElement> & {
  baseAnalyticsElementId: AnalyticsElementId;
};

/** Shared public header: one set of destinations and one responsive breakpoint. */
export function Navigation({ baseAnalyticsElementId, className, ...props }: NavigationProps) {
  const [pathname] = useLocation();
  const headerRef = useRef<HTMLElement>(null);
  const elementId = (suffix: string) => deriveAnalyticsElementId(baseAnalyticsElementId, suffix);

  useEffect(() => {
    const desktop = window.matchMedia(publicNavigationDesktopQuery);
    let lastHeaderFocus: HTMLElement | null = null;
    const belongsToHeader = (element: HTMLElement) =>
      headerRef.current?.contains(element) || Boolean(element.closest("[data-public-navigation-menu]"));
    const rememberFocus = (event: FocusEvent) => {
      if (!(event.target instanceof HTMLElement)) return;
      if (belongsToHeader(event.target)) lastHeaderFocus = event.target;
      else if (event.target !== document.body) lastHeaderFocus = null;
    };
    const restoreVisibleFocus = () => {
      // Chromium can move focus to body before matchMedia reports the breakpoint.
      const focused = document.activeElement === document.body ? lastHeaderFocus : document.activeElement;
      if (
        focused instanceof HTMLElement &&
        focused.isConnected &&
        belongsToHeader(focused) &&
        focused.getClientRects().length === 0
      ) {
        focusPublicMain();
      }
    };
    document.addEventListener("focusin", rememberFocus);
    desktop.addEventListener("change", restoreVisibleFocus);
    return () => {
      document.removeEventListener("focusin", rememberFocus);
      desktop.removeEventListener("change", restoreVisibleFocus);
    };
  }, []);

  return (
    <header ref={headerRef} className={cn("public-navigation", className)} {...props}>
      <MobileNavigation pathname={pathname} baseAnalyticsElementId={elementId("mobile-navigation")} />
      <nav className="public-navigation-desktop text-body-md-bold" aria-label="Site navigation">
        <div className="public-navigation-group">
          {primaryNavigationLinks.map((link) => (
            <PublicNavigationLink
              key={link.analyticsSuffix}
              link={link}
              pathname={pathname}
              analyticsElementId={elementId(link.analyticsSuffix)}
            />
          ))}
        </div>
        <PublicNavigationLink
          analyticsElementId={elementId("home")}
          link={homeNavigationLink}
          pathname={pathname}
          className="public-navigation-home"
        >
          <Logo variant="symbol" className="public-navigation-logo" imageSizes="40px" />
        </PublicNavigationLink>
      </nav>
      <div className="public-navigation-sign-in text-body-md-bold">
        <PublicNavigationLink
          analyticsElementId={elementId("sign-in")}
          link={signInNavigationLink}
          pathname={pathname}
        />
      </div>
    </header>
  );
}
